import {
	faBell as faBellOutline,
	faCheck,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { faBell } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Toast, Tooltip, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback } from 'react';
import { useBackInStockRequest } from '../hooks/useBackInStockRequest';

interface NotifyMeButtonProps {
	itemId: number;
	/**
	 * Matches the AddToCartControl variant it sits in:
	 * - 'floating': a round 36px bell beside the out-of-stock marker on a
	 *   product image (catalog grid and list, variant rows, dashboard cards).
	 * - 'compact': a small full-width button under the "Out of stock" pill.
	 * - 'full': a full-size button with a line of explanation, on the product
	 *   page.
	 */
	variant: 'compact' | 'full' | 'floating';
}

/**
 * PD-14306 — "Notify me": a signed-in customer asks to be told when an
 * out-of-stock item is back in stock for the account they are shopping as.
 *
 * Once asked, the control shows that plainly and a second click cancels the
 * request. The first time a customer asks, the server may also say to ask
 * whether they want a text; that question is shown by the layout's
 * BackInStockTextPrompt.
 *
 * Renders nothing unless it can actually work: a signed-in customer with an
 * account selected, on a server that has the feature. The caller only mounts
 * it where the item shows as out of stock, which is also what keeps it off
 * storefronts that hide stock (they are never told an item is out).
 */
export function NotifyMeButton({
	itemId,
	variant,
}: NotifyMeButtonProps): JSX.Element | null {
	const { t } = useTranslation();
	const { isAvailable, isRequested, isSaving, request, cancel } =
		useBackInStockRequest(itemId);

	const handleClick = useCallback(
		(e: React.MouseEvent): void => {
			// The control sits inside cards that open the product when clicked.
			e.stopPropagation();
			e.preventDefault();

			void (async (): Promise<void> => {
				if (isRequested) {
					if (!(await cancel())) {
						Toast.show({
							type: 'error',
							text1: t('Could not cancel the notification'),
							text2: t('Please try again.'),
						});
					}
					return;
				}

				const failure = await request();

				if (failure != null) {
					Toast.show({
						type: 'error',
						text1: t('Could not set up the notification'),
						text2: t(failure),
					});
				}
			})();
		},
		[isRequested, cancel, request, t],
	);

	if (!isAvailable) {
		return null;
	}

	const hint = isRequested
		? t('We will notify you when this is back in stock. Click to cancel.')
		: t('Notify me when this is back in stock');

	if (variant === 'floating') {
		return (
			<Tooltip content={hint} position="top">
				<span>
					<button
						type="button"
						onClick={handleClick}
						disabled={isSaving}
						aria-pressed={isRequested}
						aria-label={hint}
						aria-busy={isSaving}
						className={`w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-colors disabled:opacity-60 ${
							isRequested
								? 'bg-portal-accent text-white hover:bg-portal-accent-hover'
								: 'bg-brand-surface border border-brand-border text-portal-accent hover:bg-portal-accent/10'
						}`}
					>
						<FontAwesomeIcon
							icon={isSaving ? faSpinner : isRequested ? faBell : faBellOutline}
							spin={isSaving}
							className="text-sm"
						/>
					</button>
				</span>
			</Tooltip>
		);
	}

	if (variant === 'compact') {
		return (
			<Tooltip content={hint} position="top">
				<button
					type="button"
					onClick={handleClick}
					disabled={isSaving}
					aria-pressed={isRequested}
					aria-busy={isSaving}
					className={`flex items-center justify-center gap-1.5 w-full px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-60 whitespace-nowrap ${
						isRequested
							? 'bg-portal-accent/10 border-portal-accent/40 text-portal-accent'
							: 'bg-brand-surface border-brand-border text-portal-accent hover:bg-portal-accent/10'
					}`}
				>
					<FontAwesomeIcon
						icon={isSaving ? faSpinner : isRequested ? faCheck : faBellOutline}
						spin={isSaving}
						className="text-[11px]"
					/>
					<span>{isRequested ? t('We will notify you') : t('Notify me')}</span>
				</button>
			</Tooltip>
		);
	}

	// Full size, on the product page: room to say what will happen.
	if (isRequested) {
		return (
			<div className="flex items-center gap-3 rounded-lg border border-portal-accent/40 bg-portal-accent/10 px-4 py-3">
				<FontAwesomeIcon icon={faBell} className="text-portal-accent" />
				<p className="flex-1 text-sm font-medium text-default">
					{t('We will notify you when this is back in stock')}
				</p>
				<button
					type="button"
					onClick={handleClick}
					disabled={isSaving}
					aria-busy={isSaving}
					className="text-sm font-medium text-portal-accent hover:underline min-h-touch px-2 disabled:opacity-60"
				>
					{isSaving ? (
						<FontAwesomeIcon icon={faSpinner} spin />
					) : (
						t('Cancel')
					)}
				</button>
			</div>
		);
	}

	return (
		<div>
			<button
				type="button"
				onClick={handleClick}
				disabled={isSaving}
				aria-busy={isSaving}
				className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-brand-surface border border-portal-accent text-portal-accent font-medium rounded-lg transition-colors min-h-touch hover:bg-portal-accent/10 disabled:opacity-60"
			>
				<FontAwesomeIcon
					icon={isSaving ? faSpinner : faBellOutline}
					spin={isSaving}
				/>
				<span>{t('Notify me when it is back')}</span>
			</button>
			<p className="text-xs mt-2 text-subtle">
				{t('We will let you know when this item is back in stock.')}
			</p>
		</div>
	);
}
